import React from 'react';
import { useGetCeleryScheduleQuery, useGetCeleryWorkersQuery } from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

// "in 5 minutes" / "in 2 days" style relative label for a future ISO timestamp.
function relativeToNow(iso) {
  if (!iso) return '—';
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return 'due now';
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `in ${mins} min${mins === 1 ? '' : 's'}`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

function fmtUptime(seconds) {
  if (seconds == null) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

export default function CeleryJobs() {
  const { data: schedule, isFetching: scheduleLoading, error: scheduleError, refetch: refetchSchedule } = useGetCeleryScheduleQuery();
  const {
    data: workerData, isFetching: workersLoading, error: workersError, refetch: refetchWorkers,
  } = useGetCeleryWorkersQuery(undefined, { pollingInterval: 20000 });

  const entries = schedule?.entries || [];
  const workers = workerData?.workers || [];

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Header */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.5rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.65rem', flexShrink: 0 }}>
            <i className="bi bi-arrow-repeat" style={{ color: brand.goldenrod, fontSize: '1.5rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Super Admin
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0 }}>Celery Jobs</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: '4px 0 0', fontSize: '0.85rem' }}>
              Registered scheduled/recurring background jobs and live worker status.
            </p>
          </div>
        </div>
      </div>

      {/* Scheduled jobs */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginBottom: '1.5rem' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <i className="bi bi-calendar-week" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Scheduled Jobs</h5>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600 }}>{entries.length} registered</span>
          <button
            type="button" onClick={refetchSchedule}
            style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: 'white', cursor: 'pointer' }}
          >
            <i className="bi bi-arrow-clockwise me-1" />Refresh
          </button>
        </div>

        <div style={{ padding: '0.75rem 1.25rem 0', color: brand.charcoal, fontSize: '0.78rem' }}>
          This reflects the schedule currently defined in code — the separate Celery beat process only re-reads it on
          its own restart, so if it hasn&apos;t restarted since a schedule change, the two can briefly disagree.
        </div>

        {scheduleError ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: brand.red }}>Could not load the schedule.</div>
        ) : scheduleLoading && entries.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}><div className="spinner-border" style={{ color: brand.tealBlue }} /></div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '1.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>No periodic tasks registered.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: '0.825rem' }}>
              <thead style={{ background: brand.softGray }}>
                <tr>
                  <th style={{ padding: '0.5rem 1.25rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Job</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Task</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Schedule</th>
                  <th style={{ padding: '0.5rem 1.25rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Next run</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.name}>
                    <td style={{ padding: '0.55rem 1.25rem', verticalAlign: 'middle', fontWeight: 600, color: brand.deepBlue }}>{e.name}</td>
                    <td style={{ padding: '0.55rem', verticalAlign: 'middle', color: brand.charcoal, fontFamily: 'monospace', fontSize: '0.78rem' }}>{e.task}</td>
                    <td style={{ padding: '0.55rem', verticalAlign: 'middle' }}>
                      <div style={{ color: brand.deepBlue }}>{e.schedule_human}</div>
                      <div style={{ color: brand.charcoal, fontSize: '0.7rem', fontFamily: 'monospace' }}>{e.schedule_raw}</div>
                    </td>
                    <td style={{ padding: '0.55rem 1.25rem', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: brand.deepBlue }}>{relativeToNow(e.next_run)}</div>
                      <div style={{ color: brand.charcoal, fontSize: '0.72rem' }}>{fmtDateTime(e.next_run)} UTC</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Worker status */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <i className="bi bi-hdd-network" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Worker Status</h5>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600 }}>
            {workersLoading ? 'Checking…' : workerData?.available ? `${workers.length} online` : 'Unreachable'}
          </span>
          <button
            type="button" onClick={refetchWorkers}
            style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: 'white', cursor: 'pointer' }}
          >
            <i className="bi bi-arrow-clockwise me-1" />Refresh
          </button>
        </div>

        {workersError || (workerData && !workerData.available) ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
            <i className="bi bi-plug" style={{ fontSize: '2rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
            No live worker data — the broker or worker process may be unreachable right now.
          </div>
        ) : workersLoading && workers.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}><div className="spinner-border" style={{ color: brand.tealBlue }} /></div>
        ) : (
          <div style={{ padding: '1.25rem' }}>
            {workers.map((w) => (
              <div key={w.name} style={{ border: `1px solid ${brand.softGray}`, borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: brand.green, display: 'inline-block' }} />
                  <strong style={{ color: brand.deepBlue }}>{w.name}</strong>
                  <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>uptime {fmtUptime(w.uptime_seconds)}</span>
                  <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>concurrency {w.pool_concurrency ?? '—'}</span>
                  {w.active_tasks.length > 0 && (
                    <span style={{ background: 'rgba(208,155,44,0.18)', color: brand.goldenrod, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
                      {w.active_tasks.length} running now
                    </span>
                  )}
                </div>

                {w.active_tasks.length > 0 && (
                  <div style={{ marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                    {w.active_tasks.map((t) => (
                      <div key={t.id} style={{ color: brand.charcoal, fontFamily: 'monospace', fontSize: '0.75rem' }}>{t.name} ({t.id})</div>
                    ))}
                  </div>
                )}

                <div style={{ color: brand.charcoal, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
                  Runs since worker started
                </div>
                {Object.keys(w.task_counts).length === 0 ? (
                  <div style={{ color: brand.charcoal, fontSize: '0.8rem' }}>No tasks run yet.</div>
                ) : (
                  <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                    <tbody>
                      {Object.entries(w.task_counts).sort((a, b) => b[1] - a[1]).map(([task, count]) => (
                        <tr key={task}>
                          <td style={{ color: brand.charcoal, fontFamily: 'monospace', fontSize: '0.75rem' }}>{task}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: brand.deepBlue, width: 80 }}>{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
